import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import classNames from 'classnames';
import PortalContainer from '../../popups/PortalContainer';
import DataList from '../../list/DataList';
import SearchBox from './SearchBox';
import { getFontAwesomeIcon, isFontawesomeIcon } from '../../../../utils';
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
/**
 *
 * @export
 *
 * This component is used to create a select box with pagination (infinite scrolling) & search/filter options.
 * Support keyboard interactions
 *  - Arrow Keys (up & down) - navigate through options list
 *  - Enter Key - Select current highlighted option
 *  - Escape Key - Exit select mode & discard changes
 * @example
 * ```
 *  <DynamicSelect
 *      selected={selected}
 *      options={(max: number, pageToken: string, args?: any) => getOptions(max, pageToken, args)}
 *      onChange={(value) => { setSelected(value)}}
 *      placeholder=" -- select --"
 *      labelField="label"
 *      timeout={500}
 *  />
 * ```
 *
 */
const DynamicSelect = (props) => {
    // props
    let { selected, options, placeholder, className, isValid, onChange, pageSize = 6, renderOption, labelField, timeout, type = 'select-box', showEndOfContent, iconField } = props;
    // states
    let [selectedOption, setSelectedOption] = React.useState(null);
    let [showPicker, setShowPicker] = React.useState(false);
    let [coords, setCoords] = React.useState({});
    let [query, setQuery] = React.useState("");
    let [selIndex, setSelIndex] = React.useState(0);
    let [args, setArgs] = React.useState({});
    // refs
    let buttonRef = React.useRef(null);
    let optionContainerRef = React.useRef(null);
    // using ref because of async behavior of states 
    // need to access this on key up. state may not be updated by then. therefore used a ref
    let selectedOpt = React.useRef(null);
    // timer 
    let timer = React.useRef(null);
    const hasIcon = (!!iconField && (iconField === null || iconField === void 0 ? void 0 : iconField.trim().length) > 0);
    // set selected option on change selected prop
    React.useEffect(() => {
        let selOpt = { label: "", value: "" };
        if (placeholder && ((!type || (type && type == "select-box"))))
            selOpt = { label: placeholder, value: "" };
        if (selected) {
            selOpt = { label: selected, value: selected };
        }
        setSelectedOption(selOpt);
    }, [selected]);
    // get selected item &scroll it to the view on change selIndex
    React.useEffect(() => {
        let options = getOptions();
        let selOpt = options[selIndex];
        if (selOpt) {
            selectedOpt.current = JSON.parse(selOpt.dataset.value);
            selOpt.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }
    }, [selIndex]);
    // handle keyboard inputs
    const handleKeyboardInputs = (e) => {
        // e.preventDefault()
        // e.stopPropagation()
        let options = getOptions();
        // scroll 
        if (e.keyCode == 40) {
            setSelIndex(prev => (prev + 1 < options.length ? prev + 1 : options.length - 1));
        }
        if (e.keyCode == 38) {
            setSelIndex(prev => (prev - 1 < 0 ? 0 : prev - 1));
        }
        // discard on esc
        if (e.keyCode == 27) {
            closePicker();
            setQuery("");
        }
        // change on enter
        if (e.keyCode == 13) {
            // get options 
            let options = getOptions();
            if (options.length > 0) {
                if (!selectedOpt.current) {
                    let selOpt = options[0];
                    if (selOpt) {
                        selectedOpt.current = JSON.parse(selOpt.dataset.value);
                    }
                }
                onChange(selectedOpt.current);
                closePicker();
            }
        }
    };
    // filter out option dom nodes as html elements 
    const getOptions = () => {
        let container = optionContainerRef.current;
        let options = [];
        if (container) {
            options = Array.from(container === null || container === void 0 ? void 0 : container.querySelectorAll(".uxp-select-option-container"));
        }
        return options;
    };
    // close picker
    const closePicker = () => {
        setQuery("");
        setSelIndex(0);
        setShowPicker(false);
    };
    // handle click on placeholder
    const handleClick = () => {
        const buttonDetails = buttonRef.current.getBoundingClientRect();
        const minHeight = 260;
        const { top, left, right, bottom, width, height, x, y } = buttonDetails;
        const { innerWidth, innerHeight } = window;
        let _coords = { width };
        let vPos = 'top';
        let hPos = 'left';
        if (top + minHeight > innerHeight)
            vPos = 'bottom';
        if (left + width > innerWidth)
            hPos = 'right';
        if (vPos == 'top')
            _coords.top = top;
        else
            _coords.bottom = Math.max(innerHeight - bottom - window.scrollY, 0);
        if (hPos == 'left')
            _coords.left = left;
        else
            _coords.right = Math.max(innerWidth - right, 0);
        setShowPicker(true);
        setCoords(_coords);
    };
    //handle select 
    const handleSelect = (option, key) => {
        onChange(option);
        closePicker();
        setSelIndex(key);
    };
    // handle input change
    const handleInputChange = (val) => {
        setQuery(val);
        setSelIndex(0);
        selectedOpt.current = null;
        if (timer.current) {
            clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
            setArgs({ query: val });
            timer.current = null;
        }, timeout ? timeout : 500);
    };
    function isActive() {
        if (selectedOption && selectedOption.value) {
            if (typeof selectedOption.value == 'string' && selectedOption.value.trim().length > 0) {
                return true;
            }
            return true;
        }
        return false;
    }
    function getIcon(opt) {
        if (!opt)
            return null;
        if (!opt[iconField] || opt[iconField].trim().length == 0)
            return null;
        let icon = null;
        if (isFontawesomeIcon(opt[iconField])) {
            const parts = getFontAwesomeIcon(opt[iconField]);
            icon = _jsx("div", { className: "fa-icon", children: _jsx(FontAwesomeIconWrapper, { icon: parts }) });
        }
        else {
            icon = _jsx("div", { className: "image-icon", children: _jsx("div", { className: "icon", style: {
                        backgroundImage: `url(${opt[iconField]})`
                    } }) });
        }
        return icon;
    }
    // return
    return (_jsxs("div", { className: classNames("uxp-form-select"), children: [type && type == "search-box" ?
                _jsx("div", { className: "", ref: buttonRef, onClick: handleClick, children: _jsx(SearchBox, { value: (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label) || "", onChange: () => { }, className: "dynamic-select-search-box" }) })
                : _jsx("div", { className: classNames("uxp-select-placeholder", "dynamic-select", className, { "valid": isValid, "invalid": isValid == false, "show-dropdown": showPicker, "active": isActive() }), ref: buttonRef, onClick: handleClick, children: _jsx("div", { className: classNames("selected"), children: selectedOption && selectedOption.label ? selectedOption.label
                            : placeholder && placeholder }) }), showPicker &&
                _jsx(PortalContainer, { hasBackdrop: true, onClickBackdrop: closePicker, disableScroll: false, children: _jsxs("div", { className: "uxp-select-dropdown-container dynamic-select", style: coords, onKeyDown: handleKeyboardInputs, children: [_jsx(SearchBox, { value: query, onChange: handleInputChange, placeholder: selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label, autoFocus: true, className: "dynamic-select-search-box dropdown" }), _jsx("div", { className: "uxp-select-dropdown", ref: optionContainerRef, children: _jsx(DataList, { data: options, pageSize: pageSize, renderItem: (opt, key) => {
                                        let icon = getIcon(opt);
                                        return _jsx("div", { className: classNames("uxp-select-option-container", { "selected": selectedOption.label === opt[labelField], "highlighted": key == selIndex }), onClick: () => handleSelect(opt, key), onMouseOver: () => setSelIndex(key), "data-value": JSON.stringify(opt), children: renderOption
                                                ? renderOption(opt, key)
                                                : _jsxs("div", { className: classNames("uxp-select-option", { 'has-icon': hasIcon && !!icon }), children: [hasIcon && _jsxs("div", { className: "icon", children: [" ", icon, " "] }), _jsx("div", { className: "label", children: opt[labelField] })] }) }, key);
                                    }, showFooter: false, args: args, showEndOfContent: showEndOfContent }) })] }) })] }));
};
export default DynamicSelect;
